import React, { useState, useEffect } from 'react';
import axiosInstance from '../../utils/axiosInstance';
import Calendar from "./Calendar";
import './Events.css';

function Events() {
  const [events, setEvents] = useState([]);
  const [view, setView] = useState('calendar'); 
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [expandedCard, setExpandedCard] = useState(null);

  // Helper function to parse UTC date string as local date
  // This prevents timezone conversion from shifting the day
  const parseAsLocalDate = (dateString) => {
    const date = new Date(dateString);
    // Extract the UTC year, month, and day
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth();
    const day = date.getUTCDate();
    // Create a new date in local timezone with those values
    return new Date(year, month, day);
  };

  useEffect(() => {
    window.scrollTo(0, 0); 
    async function fetchEvents() {
      try {
        const response = await axiosInstance.get('/events');
        let visibleEvents = response.data.filter(event => event.visibleToCustomer);
        
        // Each recurring event instance is already a separate database record with its own date
        // We don't need to expand recurrenceDates - just use the event as-is
        // Keep the image as-is (don't add a placeholder if it's missing)
        visibleEvents = visibleEvents.map(event => ({
          ...event,
          image: event.image
        }));
        
        // Sort events by date
        visibleEvents.sort((a, b) => new Date(a.date) - new Date(b.date));
        
        setEvents(visibleEvents);
        
        const currentDate = new Date();
        const filteredUpcomingEvents = visibleEvents.filter(event => new Date(event.date) >= currentDate);
        setUpcomingEvents(filteredUpcomingEvents);
      } catch (error) {
        console.error('Error fetching events:', error);
      }
    }
    fetchEvents();
  }, []);  

  const handleViewChange = (view) => {
    setView(view);
  };

  const handleCardClick = (eventId) => {
    // If clicking the same card that's already expanded, collapse it
    if (expandedCard === eventId) {
      setExpandedCard(null);
    } else {
      // Otherwise, expand the clicked card and collapse any other expanded card
      setExpandedCard(eventId);
    }
  };

  const renderHeaderButtons = () => (
    <div className="header-buttons">
      <button className={view === 'cards' ? 'selected' : ''} onClick={() => handleViewChange('cards')}>
        <i className="fa fa-bars"></i> List View
      </button>
      <button className={view === 'calendar' ? 'selected' : ''} onClick={() => handleViewChange('calendar')}>
        <i className="fa fa-calendar"></i> Calendar View
      </button>
    </div>
  );

  const renderEventsCards = () => (
    <div className="events-cards-container">
      {upcomingEvents.map((event, index) => {
        // Extract just the time portion from start_time and end_time
        // The event.date field has the correct date for when this event occurs
        const startTime = new Date(event.start_time);
        const endTime = new Date(event.end_time);
        
        // Parse the event date as local to avoid timezone shifts
        const eventDate = parseAsLocalDate(event.date);
        
        // Create datetime objects using the correct event date with the time portion
        const displayStartTime = new Date(eventDate);
        displayStartTime.setHours(startTime.getHours(), startTime.getMinutes(), 0, 0);
        
        const displayEndTime = new Date(eventDate);
        displayEndTime.setHours(endTime.getHours(), endTime.getMinutes(), 0, 0);
        
        return (
          <div 
            className={`event-card ${expandedCard === event._id ? 'expanded' : ''}`}
            key={index}
            onClick={() => handleCardClick(event._id)}
          >
            {/* Only show image section if the event has an image */}
            {event.image && (
              <div className="event-card-image">
                <img src={event.image} alt={event.name} />
              </div>
            )}
            <div className="event-card-info">
              <div className="event-card-header">
                <div className="event-card-title">{event.name}</div>
                <div className="event-card-date">
                  {eventDate.toLocaleDateString(undefined, { 
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </div>
              </div>
              <div className="event-card-details">
                <div className="event-card-time">
                  <i className="fa fa-clock-o"></i> {displayStartTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - {displayEndTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
                <div className="event-card-location">
                  <i className="fa fa-map-marker"></i> {event.location.name}
                </div>
              </div>
              <div className={`event-card-description ${expandedCard === event._id ? 'expanded' : ''}`}>
                {event.description}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderEventCalendarContent = () => {
    return events.map((event, index) => {
      // Get the time components from the original start_time and end_time
      const originalStart = new Date(event.start_time);
      const originalEnd = new Date(event.end_time);
      
      // Parse the event date as local to avoid timezone shifts
      const eventDate = parseAsLocalDate(event.date);
      
      // Create new datetime objects combining the recurrence date with the time
      const startDateTime = new Date(eventDate);
      startDateTime.setHours(originalStart.getHours(), originalStart.getMinutes(), 0, 0);
      
      const endDateTime = new Date(eventDate);
      endDateTime.setHours(originalEnd.getHours(), originalEnd.getMinutes(), 0, 0);
      
      // Create a unique ID by combining the event ID with the date timestamp
      // This ensures each recurring instance has its own unique identifier
      const uniqueId = `${event._id}-${eventDate.getTime()}`;
      
      // Determine event color based on type
      // Public events are blue, Private events are dark purple
      const eventColor = event.type && event.type.toLowerCase().includes('private') 
        ? '#7b68a6'  // Dark purple for private events
        : '#6fb4f0'; // Blue for public events
      
      return {
        id: uniqueId,
        title: event.name,
        start: startDateTime,  // Now includes both date and time
        end: endDateTime,      // Now includes both date and time
        description: event.description,
        image: event.image,
        location: event.location.name,
        start_time: event.start_time,  // Keep for display purposes
        end_time: event.end_time,       // Keep for display purposes
        backgroundColor: eventColor,    // Set color based on event type
        borderColor: eventColor         // Match border to background
      };
    });
  };
  
  return (
    <>
      <div className="events-container">
        <h1 className="events-header">Upcoming Events & Daily Themes</h1>
        {renderHeaderButtons()}
        {view === 'cards' ? renderEventsCards() : <Calendar events={renderEventCalendarContent()} />}
      </div>
      <div style={{ 
          aspectRatio: "960/90", 
          backgroundImage: "url('/blueToPinkFooter.png')",
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center"
      }}></div>
    </>
  );
}

export default Events;
